import React, { useEffect, useMemo, useState } from "react";
import { CaretRight, Buildings, Globe, MapPin, Factory, Storefront } from "@phosphor-icons/react";

const sortNodes = (a, b) => (a.order_index || 0) - (b.order_index || 0) || a.label.localeCompare(b.label);

function iconForNode(node) {
  if (node.ref_type === "company") return Storefront;
  if (node.ref_type === "installation") return Factory;
  if (node.level_number === 1) return Globe;
  if (node.level_number >= 5) return Buildings;
  return MapPin;
}

// Índices: hijos por padre + conteo de empresas descendientes
function useTreeIndex(nodes) {
  return useMemo(() => {
    const byParent = { root: [] };
    nodes.forEach(n => { const k = n.parent_id || "root"; (byParent[k] = byParent[k] || []).push(n); });
    Object.values(byParent).forEach(arr => arr.sort(sortNodes));
    const counts = {};
    const count = (n) => {
      if (counts[n.id] !== undefined) return counts[n.id];
      const own = n.ref_type === "company" ? 1 : 0;
      counts[n.id] = own + (byParent[n.id] || []).reduce((s, c) => s + count(c), 0);
      return counts[n.id];
    };
    nodes.forEach(count);
    return { byParent, counts };
  }, [nodes]);
}

// Nodos visibles por búsqueda (coincidencias + sus ancestros)
function useSearchMatches(nodes, query) {
  return useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
    const keep = new Set();
    nodes.forEach(n => {
      const hay = `${n.label} ${n.ref_meta?.code || ""}`.toLowerCase();
      if (!hay.includes(q)) return;
      let cur = n;
      while (cur && !keep.has(cur.id)) { keep.add(cur.id); cur = cur.parent_id ? byId[cur.parent_id] : null; }
    });
    return keep;
  }, [nodes, query]);
}

export const StructureTree = ({ detail, query, activeNodeId, isActiveStructure, onSelect }) => {
  const nodes = detail?.nodes || [];
  const { byParent, counts } = useTreeIndex(nodes);
  const matches = useSearchMatches(nodes, query);
  const [expanded, setExpanded] = useState({});

  // Expandir la ruta hasta el nodo activo
  useEffect(() => {
    if (!isActiveStructure || !activeNodeId) return;
    const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
    const open = {};
    let cur = byId[activeNodeId]?.parent_id ? byId[byId[activeNodeId].parent_id] : null;
    while (cur) { open[cur.id] = true; cur = cur.parent_id ? byId[cur.parent_id] : null; }
    setExpanded(p => ({ ...p, ...open }));
  }, [nodes, activeNodeId, isActiveStructure]);

  const flat = [];
  const dfs = (list, depth) => list.forEach(n => {
    if (matches && !matches.has(n.id)) return;
    const children = byParent[n.id] || [];
    const isOpen = matches ? true : !!expanded[n.id];
    flat.push({ node: n, depth, hasChildren: children.length > 0, isOpen });
    if (children.length && isOpen) dfs(children, depth + 1);
  });
  dfs(byParent.root || [], 0);

  if (nodes.length === 0) return <p className="px-4 py-6 text-sm text-neutral-400 italic text-center">Sin nodos registrados.</p>;
  if (flat.length === 0) return <p className="px-4 py-6 text-sm text-neutral-400 text-center" data-testid="structure-tree-empty-search">Sin coincidencias.</p>;

  const levelLabel = (n) => (detail.levels || []).find(l => l.number === n.level_number)?.label;

  return (
    <ul className="py-1" data-testid="structure-tree">
      {flat.map(({ node, depth, hasChildren, isOpen }) => {
        const Icon = iconForNode(node);
        const selected = isActiveStructure && activeNodeId === node.id;
        return (
          <li key={node.id} data-testid={`nav-node-${node.id}`}>
            <div
              className={`group flex items-center gap-2 pr-4 py-2.5 rounded-lg mx-2 transition-colors duration-150 ${selected ? "bg-blue-50" : "hover:bg-neutral-50"}`}
              style={{ paddingLeft: depth * 20 + 8 }}
            >
              <button
                onClick={() => hasChildren && setExpanded(p => ({ ...p, [node.id]: !isOpen }))}
                className={`w-5 h-5 flex items-center justify-center shrink-0 text-neutral-500 ${hasChildren ? "hover:text-neutral-900" : "invisible"}`}
                aria-label={isOpen ? "Contraer" : "Expandir"}
                data-testid={`toggle-node-${node.id}`}
              >
                <CaretRight size={13} weight="bold" className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}/>
              </button>
              <Icon size={17} weight="duotone" className="text-blue-600 shrink-0"/>
              <button onClick={() => onSelect(node.id)} className="flex-1 min-w-0 text-left" title={levelLabel(node)} data-testid={`select-node-${node.id}`}>
                <span className={`block truncate text-[15px] ${selected ? "font-bold text-blue-700" : depth === 0 ? "font-bold text-neutral-800" : "font-semibold text-neutral-700"}`}>
                  {node.label}
                  {node.ref_meta?.code && <span className="ml-1.5 text-xs font-normal text-neutral-400">[{node.ref_meta.code}]</span>}
                </span>
              </button>
              {counts[node.id] > 0 && node.ref_type !== "company" && (
                <span className="text-xs font-semibold text-neutral-500 tabular-nums" data-testid={`node-count-${node.id}`}>{counts[node.id]}</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default StructureTree;
